// app/ningenie/lib/ningenie-user.ts
// baby: User ID management - uses Firebase UID when authenticated, mock when not

import { auth } from '@/app/lib/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { NINGENIE_MOCK_USER } from './ningenie-seed'

const sanitizeId = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, '_')

// baby: wait for auth to be ready (returns promise that resolves when auth state is known)
const waitForAuth = (): Promise<void> => {
  return new Promise((resolve) => {
    // baby: check if auth already has a user or is definitely null (after initial load)
    // Firebase auth initializes synchronously, but we check immediately
    if (auth.currentUser !== null || auth.currentUser === null) {
      // baby: auth is initialized (either has user or is null), resolve after small delay
      setTimeout(() => resolve(), 50)
      return
    }
    
    // baby: wait for auth state change as backup
    let resolved = false
    const unsubscribe = onAuthStateChanged(auth, () => {
      if (!resolved) {
        resolved = true
        unsubscribe()
        resolve()
      }
    })
    
    // baby: timeout after 1 second to prevent hanging
    setTimeout(() => {
      if (!resolved) {
        resolved = true
        unsubscribe()
        resolve()
      }
    }, 1000)
  })
}

// baby: get user ID - uses Firebase UID if authenticated, otherwise mock
// baby: on server-side, checks for serverUserId first
export const getNinGenieUserId = (): string => {
  // baby: check for server-side userId first (set by API routes)
  try {
    const { getServerUserId } = require('./ningenie-user-server')
    const serverUserId = getServerUserId()
    if (serverUserId) {
      return sanitizeId(serverUserId)
    }
  } catch {
    // baby: server module not available or no server userId set - continue with client auth
  }
  
  // baby: client-side: use Firebase auth
  try {
    const currentUser = auth.currentUser
    if (currentUser?.uid) {
      return sanitizeId(currentUser.uid)
    }
  } catch {
    // baby: auth not available (server-side without userId) - use mock
  }
  
  // baby: fallback to mock user when not authenticated
  return sanitizeId(NINGENIE_MOCK_USER.mainId)
}

// baby: check if using mock data (async to wait for auth)
export const isUsingMockData = async (): Promise<boolean> => {
  await waitForAuth()
  const currentUser = auth.currentUser
  const isMock = !currentUser?.uid
  console.log('[NinGenie] isUsingMockData check - currentUser:', currentUser?.uid || 'null', 'isMock:', isMock)
  return isMock
}

// baby: synchronous version for cases where auth is definitely ready
export const isUsingMockDataSync = (): boolean => {
  const currentUser = auth.currentUser
  return !currentUser?.uid
}

